import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// ── Test-data toggle ────────────────────────────────────────────────────────
// Practices flag rows they never want the day-to-day view to see
// (`is_test=true` on any doc: investor, fund, commitment, capital call, etc).
// Server hides those rows by default. Only when the operator flips
// `Show test data` in the top bar (persisted here in localStorage) do we
// pass `include_test=true` to every list request so QA / demo data comes back.
export const TEST_DATA_KEY = "aif_show_test_data";
export function isTestDataShown() {
  try { return window.localStorage.getItem(TEST_DATA_KEY) === "1"; }
  catch { return false; }
}
export function setTestDataShown(on) {
  try { window.localStorage.setItem(TEST_DATA_KEY, on ? "1" : "0"); } catch { /* ignore */ }
  try { window.dispatchEvent(new Event("aif-test-data-toggled")); } catch { /* ignore */ }
}

api.interceptors.request.use((cfg) => {
  if ((cfg.method || "get").toLowerCase() === "get" && isTestDataShown()) {
    cfg.params = { ...(cfg.params || {}), include_test: true };
  }
  return cfg;
});

// ── 401 handling ────────────────────────────────────────────────────────────
// Coalesce concurrent refresh attempts so 8 parallel 401s don't fire 8 refresh
// calls (race + wasted network). All in-flight retries wait on ONE promise.
let _refreshInFlight = null;

function _tryRefresh() {
  if (!_refreshInFlight) {
    _refreshInFlight = axios
      .post(`${API}/auth/refresh`, {}, { withCredentials: true })
      .finally(() => { _refreshInFlight = null; });
  }
  return _refreshInFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const cfg = err?.config;
    // Don't try to refresh a failed refresh call (avoids infinite loop).
    const isRefreshCall = typeof cfg?.url === "string" && cfg.url.includes("/auth/refresh");
    if (status === 401 && cfg && !cfg._retry && !isRefreshCall) {
      cfg._retry = true;
      try {
        await _tryRefresh();
        return api(cfg);
      } catch {
        // Refresh failed — the session is genuinely dead. Broadcast so
        // AuthProvider clears the user and ProtectedRoute redirects to /login.
        // Swallow silently to prevent CRA's dev overlay avalanche.
        try {
          window.dispatchEvent(new Event("aif-session-expired"));
        } catch { /* ignore */ }
      }
    }
    return Promise.reject(err);
  }
);

export function formatINR(amount, { compact = true } = {}) {
  if (amount == null || isNaN(amount)) return "—";
  const abs = Math.abs(amount);
  if (compact) {
    if (abs >= 1e7) return `₹ ${(amount / 1e7).toLocaleString("en-IN", { maximumFractionDigits: 2 })} Cr`;
    if (abs >= 1e5) return `₹ ${(amount / 1e5).toLocaleString("en-IN", { maximumFractionDigits: 2 })} L`;
  }
  return `₹ ${amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export function formatPct(n, digits = 2) {
  if (n == null || isNaN(n)) return "—";
  return `${n.toFixed(digits)}%`;
}

/**
 * Fetch a URL as a blob (with auth cookies) and force a download in the browser.
 * Use this for every file-download button — plain <a target="_blank"> lets the
 * browser render XML/JSON/HTML inline even when the server sets
 * `Content-Disposition: attachment`, which shows up as a "pretty-print" page
 * instead of a real download.
 *
 * @param {string} path  API path (relative to `${API}`) or absolute URL
 * @param {string} filename  suggested filename shown to the user
 */
export async function downloadFile(path, filename) {
  const url = path.startsWith("http") ? path : `${API}${path.startsWith("/") ? path : `/${path}`}`;
  const res = await api.get(url.replace(API, ""), { responseType: "blob" });
  // If the server returned an error page as JSON/HTML, res.data will still be a Blob.
  // Assume 2xx responses = real file (axios rejects non-2xx by default).
  const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Free the object URL after the click has been dispatched.
  setTimeout(() => window.URL.revokeObjectURL(objectUrl), 5000);
}
